import React, { useEffect, useMemo, ReactElement } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router';
import FormatService from '../../services/format-service';
import EncryptService from '../../services/encrypt-service';
import StructureService from '../../services/structure-service';
import useCurrentUserPermissions from '../../hooks/use-current-user-permissions';
import {
  ROUTE_PORTFOLIO_GROUP, ROUTE_SETTINGS_ACCOUNT, ROUTE_SETTINGS_ACCOUNT_LIST, ROUTE_SETTINGS_DEAL_TEMPLATE_LIST,
  ROUTE_SETTINGS_NOTIFICATIONS, ROUTE_SETTINGS_OPERATION_LOG, ROUTE_SETTINGS_PORTFOLIO_GROUP_LIST,
  ROUTE_SETTINGS_PROFILE, ROUTE_SETTINGS_SECURITY, ROUTE_SETTINGS_USER_LIST,
} from '../../constants/system/routes';
import {
  QUERY_PARAM_EMAIL_CONNECTOR_CODE, QUERY_PARAM_TAB, QUERY_PARAM_PORTFOLIO_ID, QUERY_PARAM_CODE, QUERY_PARAM_STATE,
} from '../../constants/system/query-params';
import {
  TAB_NAME_ACCOUNT, TAB_NAME_ACCOUNT_ADMINISTRATION, TAB_NAME_AUDIT_LOG, TAB_NAME_DEAL_TEMPLATES, TAB_NAME_USERS,
  TAB_NAME_NOTIFICATIONS, TAB_NAME_PORTFOLIO_LIST, TAB_NAME_PORTFOLIO_MANAGEMENT, TAB_NAME_PROFILE, TAB_NAME_SECURITY,
} from '../../constants/system/tab-names';
import { SETTINGS_ACCOUNT_VIEW } from '../../constants/entity/permissions';

const Settings: ReactMemoComponent = React.memo((): ReactElement | null => {
  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const permissionList: string[] = useCurrentUserPermissions();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const queryCode: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_CODE]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_CODE].toString()) : null;
  const queryState: JsonObject | null = locationQueryParams && locationQueryParams[QUERY_PARAM_STATE]
    ? FormatService.extractJson(EncryptService.getBase64String(locationQueryParams[QUERY_PARAM_STATE].toString()))
    : null;
  const isAccountSettingsAllowed: boolean = StructureService.checkPermission(SETTINGS_ACCOUNT_VIEW, permissionList);
  const defaultRoute: string = isAccountSettingsAllowed ? ROUTE_SETTINGS_ACCOUNT : ROUTE_SETTINGS_PROFILE;

  const TAB_NAME_ROUTE_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [TAB_NAME_ACCOUNT]: ROUTE_SETTINGS_ACCOUNT,
    [TAB_NAME_ACCOUNT_ADMINISTRATION]: ROUTE_SETTINGS_ACCOUNT_LIST,
    [TAB_NAME_AUDIT_LOG]: ROUTE_SETTINGS_OPERATION_LOG,
    [TAB_NAME_DEAL_TEMPLATES]: ROUTE_SETTINGS_DEAL_TEMPLATE_LIST,
    [TAB_NAME_PORTFOLIO_MANAGEMENT]: ROUTE_SETTINGS_PORTFOLIO_GROUP_LIST,
    [TAB_NAME_USERS]: ROUTE_SETTINGS_USER_LIST,
    [TAB_NAME_PROFILE]: ROUTE_SETTINGS_PROFILE,
    [TAB_NAME_SECURITY]: ROUTE_SETTINGS_SECURITY,
    [TAB_NAME_NOTIFICATIONS]: ROUTE_SETTINGS_NOTIFICATIONS,
  }), []);

  useEffect((): void => {
    if (queryCode && queryState) {
      const queryParams: LocationQueryParams = {
        [QUERY_PARAM_EMAIL_CONNECTOR_CODE]: queryCode,
      };

      if (queryState.portfolioId) {
        queryParams[QUERY_PARAM_TAB] = TAB_NAME_PORTFOLIO_LIST;
        queryParams[QUERY_PARAM_PORTFOLIO_ID] = queryState.portfolioId;

        navigate({
          pathname: `${ROUTE_PORTFOLIO_GROUP}/${queryState.portfolioGroupId}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      }

      if (queryState.accountId) {
        navigate({
          pathname: ROUTE_SETTINGS_ACCOUNT,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    }
  }, [queryCode]);

  if (queryCode && queryState) {
    return null;
  }

  if (queryTab && TAB_NAME_ROUTE_MAP[queryTab]) {
    const tabRoute: string = TAB_NAME_ROUTE_MAP[queryTab];
    const queryParams: LocationQueryParams = locationQueryParams || {};

    delete queryParams[QUERY_PARAM_TAB];

    return <Navigate to={`${tabRoute}${FormatService.formatQueryParams(queryParams)}`} />;
  }

  return <Navigate to={defaultRoute} />;
});

export default Settings;
